'use client';

import React from 'react';
import WithAIPanel from '../../../components/ai/WithAIPanel';
import { LearnDataVideo } from '@osmosis/shared/types/learn';
import {
  AIPanelPage,
  getPanelOptions,
} from '../../../context/AIPanelContext.utils';
import { prepareTruncatedContext } from '../../../lib/util/oai';
import { useUserContext } from '../../../context/UserContext';

type Props = {
  learnData: LearnDataVideo;
  children: React.ReactNode;
  /**
   * The "up next" sidebar panel. Passed in as a node so its SSR content is
   * rendered by the parent server component.
   */
  sidebarFooter?: React.ReactNode;
};

/**
 * Wraps the learn page content in `WithAIPanel`, nudging the AI panel rail
 * down when the admin toolbar renders above the content column (outside
 * `WithAIPanel`) so the rail's top stays aligned with the video.
 */
const LearnAIPanel = ({ learnData, children, sidebarFooter }: Props) => {
  const { user } = useUserContext();
  const hasAdminToolbar = !!Object.keys(user?.admin || {}).length;

  const isVideoPage = !!learnData.sections.video;
  const rawTranscriptText = learnData.sections.transcripts?.[0]?.text;

  return (
    <WithAIPanel
      title={learnData.name}
      pageContext={prepareTruncatedContext({
        title: learnData.name,
        rawContext: rawTranscriptText || '',
        isVideo: isVideoPage,
      })}
      panelOptions={getPanelOptions(AIPanelPage.Learn)}
      showExamplePrompts
      classNames={{
        content: 'bg-neutral-100',
        panel: hasAdminToolbar ? 'mt-12' : undefined,
      }}
      keepSidebarOpen
      matchAIHeightToContent
      sidebarFooter={sidebarFooter}
    >
      {children}
    </WithAIPanel>
  );
};

export default LearnAIPanel;
